import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  TextField,
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import CloseIcon from '@material-ui/icons/Close'
import { Autocomplete } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { axiosInstance } from '../../api'
import { useAppDialog } from '../use-app-dialog'
import { useTranslation } from 'react-i18next'
import { SimpleFunc } from '../../models'
import { drawerBackdropBlurStyle } from '../../shared/style-settings'
import { useAppDialogStyles } from './use-app-dialog.styles'
import { AppDialogVariants } from '../../store/app-dialog/types'

type IAppDialogWithBodyProps = {
  hidden?: boolean
  listedItemId: number
  donorName: string
  onExit?: () => void
  title: string
  forceClose?: boolean
  isShow?: boolean
  showCloseButton?: boolean
  actions?: React.ReactNode | null
  message?: string
  closeHandler?: SimpleFunc
  onSubmitSuccess?: SimpleFunc
  resetHandler?: () => void
  setIsShow?: (value: boolean) => void
  children?: React.ReactNode
}

type Option = {
  value: string
}

type Options = Option[]

const handleSubmit = (rejectReason: string, listedItemId: number) => {
  const body = {
    RejectReason: rejectReason,
    State: 'rejected',
  }

  return axiosInstance.post(
    `/listed-items/${listedItemId}/moderation?listedItemId=${listedItemId}`,
    body
  )
}

export const AppDialogModerationReject: React.FC<IAppDialogWithBodyProps> = (
  props
): React.ReactElement => {
  const [open, setOpen] = useState(true)
  const [commonRejectReason, setCommonRejectReason] = useState<string>('')
  const [customRejectReason, setCustomRejectReason] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    listedItemId,
    onExit,
    title,
    actions,
    showCloseButton,
    closeHandler,
    onSubmitSuccess,
    setIsShow,
  } = props
  const classes = useAppDialogStyles()
  const { setAppDialog } = useAppDialog()
  const { t } = useTranslation()
  const [textFieldLabel, setTextFieldLabel] = useState<string>('')

  // can be changed to get some options from backend or something
  const [OPTIONS] = useState<Options>([
    { value: 'Inappropriate content' },
    { value: 'Prohibited item' },
    { value: 'Too used or damaged' },
  ])

  useEffect(() => {
    setOpen(true)
    setIsShow && setIsShow(true)
  }, [])

  const onCloseHandler = (): void => {
    setOpen && setOpen(false)
    setIsShow && setIsShow(false)
    closeHandler && closeHandler()
  }

  const handleSubmit = (rejectReason: string, listedItemId: number) => {
    if (rejectReason.length > 500) {
      setAppDialog({
        isShow: true,
        title: t('moderation.error.rejectionReasonTooLong.title'),
        message: t('moderation.error.rejectionReasonTooLong.message'),
        variant: AppDialogVariants.error,
        actions: (
          <>
            <Button onClick={() => setAppDialog({ isShow: false })}>
              {t('buttons.ok')}
            </Button>
          </>
        ),
      })
      return Promise.reject(new Error('Validation failed'))
    } else {
      const body = {
        RejectReason: rejectReason,
        State: 'rejected',
      }

      return axiosInstance.post(
        `/listed-items/${listedItemId}/moderation?listedItemId=${listedItemId}`,
        body
      )
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      onExited={onExit}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        style: drawerBackdropBlurStyle,
      }}
    >
      <DialogTitle id="alert-dialog-title" className={classes.dialogTitle}>
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onCloseHandler}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <div className={classes.formContent}>
          <div className={classes.fieldBlock}>
            <FormLabel
              htmlFor="commonRejectionReasons"
              className={classes.label}
            >
              Common reasons:
            </FormLabel>
            <FormControl
              variant="outlined"
              className={classes.commonRejectionReasonSelector}
              fullWidth
            >
              <Autocomplete
                value={
                  OPTIONS.filter((item) => item.value! === textFieldLabel)[0] ||
                  null
                }
                options={OPTIONS}
                id={'filter-common-rejection-reasons'}
                getOptionLabel={(option) => option.value as string}
                //@ts-ignore
                onChange={(_, value) => {
                  if (value === null || value.value === '') {
                    setTextFieldLabel('')
                    setCommonRejectReason('')
                  } else {
                    setCommonRejectReason(value.value)
                    setTextFieldLabel(value.value)
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={textFieldLabel || 'Select a reason'}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={classes.fieldBlock}>
            <FormLabel
              htmlFor="customRejectionReason"
              className={classes.label}
            >
              Custom:
            </FormLabel>
            <TextField
              id="customRejectionReason"
              aria-label="Custom rejection reason"
              variant="outlined"
              multiline
              minRows={5}
              fullWidth
              name="customReason"
              onChange={(e) => {
                if (e.target.value === null || e.target.value.trim() === '') {
                  setCustomRejectReason('')
                } else if (
                  e.target.value !== null &&
                  e.target.value.trim() !== ''
                ) {
                  setCustomRejectReason(e.target.value)
                }
              }}
            />
          </div>
        </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {actions ? (
            actions
          ) : (
            <Button
              type="submit"
              className={classes.button}
              variant="contained"
              disabled={isSubmitting}
              onClick={() => {
                const rejectReason =
                  customRejectReason !== ''
                    ? customRejectReason
                    : commonRejectReason

                setIsSubmitting(true)
                handleSubmit(rejectReason, listedItemId)
                  .then(() => {
                    setOpen && setOpen(false)
                    setIsShow && setIsShow(false)
                    onSubmitSuccess && onSubmitSuccess()
                  })
                  .finally(() => setIsSubmitting(false))
              }}
              color="primary"
            >
              Ok
            </Button>
          )}
        </DialogActions>
    </Dialog>
  )
}
